/**
 * The two places this business can send email from, as a choice rather than a
 * configuration exercise.
 *
 * ## Why a choice of two and not a form
 *
 * There was already a form: host, port, user, password, four boxes on the
 * settings screen. It is the right thing to keep for the day somebody moves to
 * a relay nobody has heard of, and it is the wrong thing to put in front of an
 * administrator who has exactly two options and wants the other one. Getting
 * `smtp.gmail.com` and `587` right by hand is not a decision, it is a quiz.
 *
 * So: two named relays with their hosts and ports already correct, and the
 * boxes still there underneath for anything else.
 *
 * ## What is deliberately *not* here
 *
 * No passwords, and no usernames. A relay is a fact about infrastructure and
 * belongs in the source; a credential belongs in the encrypted settings, typed
 * by whoever owns it, and read back only by the process that sends. Putting a
 * mailbox password in this file would put it in git, which is the one place a
 * password must never be.
 */
export const MAIL_RELAYS = ['own', 'gmail', 'custom'];
/**
 * The presets.
 *
 * `own` is first because it is the one that should be used. Mail sent through
 * Gmail arrives from a gmail.com address, which means the SPF, DKIM and DMARC
 * records published for gospeednet.co.ke authorise nothing that is actually
 * being sent — every one of those records only vouches for mail claiming to
 * come *from* the domain, and none of it does.
 */
export const RELAY_PRESETS = {
    own: {
        id: 'own',
        label: 'Our own mail server',
        blurb: 'mail.gospeednet.co.ke. Mail arrives from your own domain, so the SPF and DKIM records ' +
            'published for gospeednet.co.ke actually vouch for it.',
        host: 'mail.gospeednet.co.ke',
        port: 587,
        encryption: 'starttls',
        userHint: 'The full mailbox address, for example info@gospeednet.co.ke',
    },
    gmail: {
        id: 'gmail',
        label: 'Gmail',
        blurb: 'smtp.gmail.com. Needs an App Password rather than the account password, and mail arrives ' +
            'from a gmail.com address whatever the From line says.',
        host: 'smtp.gmail.com',
        port: 587,
        encryption: 'starttls',
        userHint: 'The Gmail address, and an App Password as the password',
    },
    custom: {
        id: 'custom',
        label: 'Something else',
        blurb: 'Type the host and port yourself. For any relay not listed above.',
        host: null,
        port: null,
        encryption: null,
        userHint: 'Whatever that relay expects',
    },
};
export function isMailRelay(value) {
    return MAIL_RELAYS.includes(value);
}
/**
 * Resolve what to actually connect to.
 *
 * The preset supplies the host and port unless the choice is `custom`, in which
 * case whatever was typed is used. A stored host is still honoured even for a
 * named preset, so an operator who needs `mail2.` for an afternoon is not
 * blocked by a constant — but they have to have typed it, and the absence of a
 * typed value means the preset, not nothing.
 */
export function resolveRelay(choice, stored) {
    const preset = RELAY_PRESETS[choice];
    const host = stored.host?.trim() || preset.host;
    const port = stored.port ?? preset.port;
    return {
        host: host ?? null,
        port: port ?? null,
        // 465 is encrypted from the first byte; everything else negotiates upward.
        secure: port === 465,
    };
}
//# sourceMappingURL=mail-relays.js.map